import styled from 'styled-components'
import { useState } from 'react'
import { Navigation, Thumbs, FreeMode } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Image as NextImage } from '@/features/shared/elements/Image/NextImage'
import { shadow1, shadow2 } from '@/features/shared/styles/effects'
import { type Swiper as SwiperType } from 'swiper/types'
import { useHandleZoomImage } from '@/features/shared/hooks/useHandleZoomImage'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { Row } from '@shipt/design-system-layouts'

type Props = {
  slideImages: string[]
  imageOpacity: string
  productName: string
  productSize: string
}

export const ThumbsCarousel = ({
  slideImages,
  imageOpacity = '',
  productName = '',
  productSize = '',
}: Props) => {
  const { handlePanZoomClick, handlePanZoomMove, isZooming } = useHandleZoomImage()
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)

  const preloadMainPdpImageByIndex = (index: number) => {
    const imageToPreload = slideImages[index]
    if (imageToPreload) new Image().src = imageToPreload
  }

  return (
    <Row margin={['xl', 0]}>
      <Prev className="swiper-button-prev" />
      <Next className="swiper-button-next" />
      <SwiperContainerThumbs
        className="swiper-container-thumbs"
        modules={[Navigation, Thumbs, FreeMode]}
        onSwiper={setThumbsSwiper}
        slidesPerView={4}
        spaceBetween={16}
        watchOverflow
        watchSlidesProgress
        direction="vertical"
        freeMode
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }}
        threshold={5}
      >
        {slideImages.map((url, i) => (
          <SwiperSlide key={url}>
            <ImageButton
              aria-label={`slide ${i + 1} of ${slideImages.length}`}
              onClick={() => trackElementClicked({ content: 'image_carousel' })}
            >
              <ThumbsProductImageWrapper>
                <ThumbsProductImage
                  src={url}
                  alt=""
                  $imageOpacity={imageOpacity}
                  width={44}
                  height={44}
                  style={{ objectFit: 'contain' }}
                />
              </ThumbsProductImageWrapper>
            </ImageButton>
          </SwiperSlide>
        ))}
      </SwiperContainerThumbs>
      <MainProductImagesWrapper
        modules={[Navigation, Thumbs, FreeMode]}
        simulateTouch={false}
        speed={0}
        thumbs={{ swiper: thumbsSwiper }}
      >
        {slideImages.map((url, i) => (
          <SwiperSlide key={url}>
            {({ isActive }) => (
              <MainImageWrapper
                onClick={handlePanZoomClick}
                onMouseMove={handlePanZoomMove}
                $isZooming={isZooming}
              >
                {!!(isActive || i === 0) && (
                  <MainProductImage
                    src={url}
                    alt={`slide ${i + 1} of ${slideImages.length}, ${productName}, ${productSize}`}
                    $imageOpacity={imageOpacity}
                    fill
                    preload={i === 0}
                    unoptimized
                    onLoad={() => preloadMainPdpImageByIndex(i + 1)}
                  />
                )}
              </MainImageWrapper>
            )}
          </SwiperSlide>
        ))}
      </MainProductImagesWrapper>
    </Row>
  )
}

const Prev = styled.div`
  top: 0;
  left: 16px;
  color: black;

  & .swiper-navigation-icon {
    transform: rotate(-90deg);
    width: 10px;
  }
`
const Next = styled.div`
  top: 350px;
  left: 16px;
  color: black;

  & .swiper-navigation-icon {
    transform: rotate(90deg);
    width: 10px;
  }
`

const SwiperContainerThumbs = styled(Swiper)`
  padding: 0.5rem;
  width: 20%;
  height: 300px;

  .swiper-slide-thumb-active > button {
    ${shadow2};
  }

  .swiper-slide > button:hover {
    ${shadow2};
  }
`

const ImageButton = styled(TransparentButton)`
  ${shadow1};
  padding: 0.5rem;
  border-radius: 0.5rem;
  width: 60px;
  height: 60px;
  position: relative;
`

const ThumbsProductImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const ThumbsProductImage = styled(NextImage)<{ $imageOpacity: string }>`
  opacity: ${({ $imageOpacity }) => $imageOpacity};
`

const MainProductImagesWrapper = styled(Swiper)`
  width: 80%;
  max-height: 300px;
`

const MainImageWrapper = styled.div<{ $isZooming: boolean }>`
  height: 300px;
  position: relative;

  &:hover {
    cursor: ${({ $isZooming }) => ($isZooming ? 'zoom-out' : 'zoom-in')};
    transform: ${({ $isZooming }) => ($isZooming ? 'scale(3)' : 'none')};
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    &:hover {
      cursor: default;
      transform: none;
    }
  }
`

const MainProductImage = styled(NextImage)<{ $imageOpacity: string }>`
  opacity: ${({ $imageOpacity }) => $imageOpacity};
  object-fit: contain;
`
